import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import * as React from 'react';
import { StyleSheet } from 'aphrodite';
import Clickable from '@khanacademy/wonder-blocks-clickable';
import { View } from '@khanacademy/wonder-blocks-core';
import { LabelXSmall, Body, LabelSmall } from '@khanacademy/wonder-blocks-typography';
import * as tokens from '@khanacademy/wonder-blocks-tokens';

const _excluded = ["id", "children", "kind", "size", "role", "onClick", "style", "tabIndex", "testId"];
const {
  semanticColor
} = tokens;
const PillInner = props => {
  const {
    children,
    size
  } = props;
  if (typeof children !== "string") {
    return children;
  }
  if (size === "small") {
    return React.createElement(LabelXSmall, null, props.children);
  }
  if (size === "large") {
    return React.createElement(Body, null, children);
  }
  return React.createElement(LabelSmall, null, children);
};
const Pill = React.forwardRef(function Pill(props, ref) {
  const {
      id,
      children,
      kind = "neutral",
      size = "medium",
      role,
      onClick,
      style,
      tabIndex,
      testId
    } = props,
    ariaProps = _objectWithoutPropertiesLoose(props, _excluded);
  let wrapperSizeStyle;
  switch (size) {
    case "small":
      wrapperSizeStyle = pillStyles.wrapperSmall;
      break;
    case "large":
      wrapperSizeStyle = pillStyles.wrapperLarge;
      break;
    default:
      wrapperSizeStyle = pillStyles.wrapperMedium;
  }
  const colorStyles = _generateColorStyles(!!onClick, kind);
  const defaultStyles = [pillStyles.wrapper, colorStyles.pill, wrapperSizeStyle];
  if (onClick) {
    return React.createElement(Clickable, _extends({
      id: id,
      role: role,
      onClick: onClick,
      style: [defaultStyles, colorStyles.clickableWrapper, style],
      testId: testId,
      ref: ref,
      tabIndex: tabIndex
    }, ariaProps), () => React.createElement(PillInner, {
      size: size
    }, children));
  }
  return React.createElement(View, _extends({
    id: id,
    role: role,
    style: [defaultStyles, style],
    testId: testId,
    ref: ref
  }, ariaProps), React.createElement(PillInner, {
    size: size
  }, children));
});
const pillStyles = StyleSheet.create({
  wrapper: {
    display: "inline-flex",
    width: "fit-content"
  },
  wrapperSmall: {
    paddingLeft: tokens.spacing.xSmall_8,
    paddingRight: tokens.spacing.xSmall_8,
    borderRadius: tokens.border.radius.radius_040,
    height: 20
  },
  wrapperMedium: {
    paddingLeft: tokens.spacing.xSmall_8,
    paddingRight: tokens.spacing.xSmall_8,
    borderRadius: tokens.border.radius.radius_040,
    height: tokens.spacing.large_24
  },
  wrapperLarge: {
    paddingLeft: tokens.spacing.small_12,
    paddingRight: tokens.spacing.small_12,
    paddingTop: tokens.spacing.xxSmall_6,
    paddingBottom: tokens.spacing.xxSmall_6,
    borderRadius: tokens.sizing.size_240,
    height: tokens.spacing.xLarge_32
  }
});
const styles = {};
const _generateColorStyles = (clickable, kind) => {
  const pillType = `${kind}-${clickable.toString()}`;
  if (styles[pillType]) {
    return styles[pillType];
  }
  let backgroundColor;
  switch (kind) {
    case "accent":
      backgroundColor = semanticColor.status.notice.foreground;
      break;
    case "info":
      backgroundColor = semanticColor.status.notice.background;
      break;
    case "success":
      backgroundColor = semanticColor.status.success.background;
      break;
    case "warning":
      backgroundColor = semanticColor.status.warning.background;
      break;
    case "critical":
      backgroundColor = semanticColor.status.critical.background;
      break;
    case "transparent":
      backgroundColor = "transparent";
      break;
    case "neutral":
    default:
      backgroundColor = semanticColor.status.neutral.background;
  }
  const pressColor = kind === "transparent" || kind === "neutral" ? tokens.color.offBlack16 : kind === "accent" ? tokens.color.activeBlue : `color-mix(in srgb, ${tokens.color.offBlack32}, ${backgroundColor})`;
  const textColor = kind === "accent" ? semanticColor.text.inverse : semanticColor.text.primary;
  const theme = {
    default: {
      border: kind === "transparent" ? `1px solid ${semanticColor.border.primary}` : "none",
      background: backgroundColor,
      foreground: textColor
    },
    focus: {
      border: semanticColor.focus.outer
    },
    hover: {
      border: semanticColor.action.primary.progressive.hover.border
    },
    press: {
      border: semanticColor.action.primary.progressive.press.border,
      background: pressColor
    }
  };
  const colorStyles = {
    pill: {
      backgroundColor: theme.default.background,
      outline: theme.default.border,
      color: theme.default.foreground,
      alignItems: "center",
      justifyContent: "center"
    },
    clickableWrapper: {
      outline: theme.default.border,
      ":hover": {
        outline: `2px solid ${theme.hover.border}`,
        outlineOffset: tokens.spacing.xxxxSmall_2
      },
      ":active": {
        backgroundColor: theme.press.background,
        outline: `2px solid ${theme.press.border}`,
        outlineOffset: tokens.spacing.xxxxSmall_2
      },
      ":focus-visible": {
        outline: `2px solid ${theme.focus.border}`,
        outlineOffset: tokens.spacing.xxxxSmall_2
      }
    }
  };
  styles[pillType] = StyleSheet.create(colorStyles);
  return styles[pillType];
};

export { Pill as default };
